/* eslint-disable complexity */
import { CustomElement, OverrideCustomElementConfig } from "@cervval.js/client-html5-core/src/components/customElement.decorator";
import { Event, EventHandler, NoBubblesEventInit } from "@cervval.js/client-html5-core/src/components/event.decorator";
import { TsDate, UnixTimestamp, UnixTimestampIndeterminateInterval, areSame, beginOfNext, getIntersectingDays, isAfter, subtract, toDate } from "@cervval.js/common/src/tools/timestamps.tools";
import { ObjectTools } from "@cervval.js/common/tools";
import { DAY_CELL_IS_CLICKED, HTMLDateDivElement } from "../calendar/calendar";
import { CjsDatePicker, DAY_CELL_SELECTABLE, DatePickerOptions, defaultDatePickerOptions } from "../datePicker/datePicker";

/** Drag selection interface */
interface DragSelection {

    /** A drag is processing */
    isOccurring: boolean;

    /** User is updating startDate or endDate during drag */
    isUpdating: "startDate" | "endDate";
}

export interface DateIntervalPickerSelection {
    /** Starting ts selected by user */
    start: UnixTimestamp | undefined;

    /**
     * Ending ts selected by user
     * ! value is beginOfNextDay than current day selected in component
     */
    finish: UnixTimestamp | undefined;
}

export const defaultDateIntervalPickerOptions = defaultDatePickerOptions;
export type DateIntervalPickerOptions = DatePickerOptions & {
    /** 
     * if this option is true, when an interval is already selected, user can click on an edge and then click on another date to move the edge
     * clickToMoveEdges = true is not very intuitive, if user wants to pick an interval with the same start, he may not understand he needs to click on the end
     */
    clickToMoveEdges?: boolean
};

const DAY_CELL_START_RANGE = "start-range";
const DAY_CELL_END_RANGE = "end-range";
const OVERED_CELL = "overed-cell";
const OVERED_CELL_FIRST = "first";
const OVERED_CELL_FIRST_ROUNDED = "first-rounded";
const OVERED_CELL_LAST = "last";
const OVERED_CELL_LAST_ROUNDED = "last-rounded";
const OVERED_CELL_SELECTED = "selected";
/**
 * @class DateIntervalPicker @extends CjsDatePicker
 * @classdesc Component for select range between two dates
 * The component detect click cell of DatePicker and follow the following scheme :
 * 1) Click on a cell with still empty display (set start date)
 * 2) Display overview of allowed interval by moving the end interval edge
 * 3) Click on cell to validate Interval (set end date)
 * 4) Click again on :
 * 4) a) One of the edges to move this edge by taking back to @see 2)
 * 4) b) cell which is not a edge then reset by taking back in @see 1)
 * The edge can only be edited by click on edge, else reset interval
 */
@CustomElement({
    name: "cjs-date-interval-picker",
    style: require("./dateIntervalPicker.scss"),
    elementCssClasses: ["cjs-calendar", "cjs-date-picker"]
    // Uses the same template as calendar (extends)
})
export class CjsDateIntervalPicker extends CjsDatePicker {


    /** When a day cell is overed by user */
    @Event(NoBubblesEventInit)
    public dayCellOveredEvent!: EventHandler<TsDate>;

    /** When a day cell is overed by user */
    @Event(NoBubblesEventInit)
    public dateChangeEvent!: EventHandler<TsDate>;

    /**
     * Store values of current range selection
     */
    private _selectedRangeDates: DateIntervalPickerSelection = {
        start: this._value,
        finish: undefined
    };

    /**
     * All dates selected by the user from start to end interval
     * TODO Optimiser la manière de déterminer cet interval. Faire le calcul qu'a partir du mois affiché (et non sur tout l'interval)
     */
    private _selectedTss: TsDate[] = [];

    /**
     * Store if drag selection is in progress 
     * with which edge is manipulated
     */
    public dragSelection: DragSelection = {
        isOccurring: false,
        isUpdating: "endDate"
    };

    /** Start cell of interval */
    private _startCell: HTMLDateDivElement | null = null;

    /** End cell of interval */
    private _endCell: HTMLDateDivElement | null = null;

    /** Copy of the initial ranges. Will be used to re-init ranges */
    private _ranges: UnixTimestampIndeterminateInterval[] = [];

    constructor(options?: Partial<DateIntervalPickerOptions>, customElementOptions?: OverrideCustomElementConfig) {
        super(options, customElementOptions);
    }

    /** When component is init */
    public override init(): void {
        // Disable keyboard inputs
        this.options.setWithoutRefresh({ enableKeyboardInput: false });

        // Save the initial range array because we will update temporary the range option on first click
        this._ranges = this.options.ranges;

        super.init();
    }

    /** Update component display */
    public override refresh(): void {
        super.refresh();
        this.updateCellsDisplay();
    }

    /** Get selected interval values */
    public getValueInterval(): DateIntervalPickerSelection | undefined {

        // Return undefined if we don't have a correct interval
        if (!this._selectedRangeDates.start || !this._selectedRangeDates.finish) {
            return undefined;
        }
        return ObjectTools.clone(this._selectedRangeDates);
    }

    /**
     * Set selected interval values and refresh display
     * @param startEndDates start and end date of the interval
     */
    public setValueInterval(startEndDates: DateIntervalPickerSelection, p?: { clearSelection?: boolean }): void {

        if (p?.clearSelection !== false) {
            // clear last selection
            this.clearSelection();
        }

        // Avoid 'Invalid interval' error
        if (startEndDates.start && startEndDates.finish && startEndDates.start > startEndDates.finish) {
            // Swap dates order
            startEndDates = { start: startEndDates.finish, finish: startEndDates.start };
        }

        // Avoid to copy the reference of the same instance
        this._selectedRangeDates = ObjectTools.clone(startEndDates);

        // If a start date is selected and (endDate || earlierDate), clear selection
        if (this._selectedRangeDates.start === this._selectedRangeDates.finish) {
            return;
        }

        // Update selected start date value
        this._startCell = this._selectedRangeDates.start ? this._getCellByDate(this._selectedRangeDates.start) : null;
        this._startCell?.classList.add(DAY_CELL_START_RANGE);

        // Update selected end date value
        this._endCell = this._selectedRangeDates.finish ? this._getCellByDate(this._selectedRangeDates.finish, { isFinishDate: true }) : null;

        this._endCell?.classList.add(DAY_CELL_END_RANGE);

        if (this._selectedRangeDates.start && this._selectedRangeDates.finish) {
            // Retrieve each days between startDate and current overedCell
            this._selectedTss = getIntersectingDays(this._selectedRangeDates.start, this._selectedRangeDates.finish, { strict: true });
        }

        // Update selectedCells
        this.updateCellsDisplay();

    }

    /**
     * Behavior when cell is clicked by user
     */
    protected override _cellClickCallback(cell: HTMLDateDivElement): void {

        if (this._clickedCell != null) {
            this._clickedCell.classList.remove(DAY_CELL_IS_CLICKED);
        } else {
            // Remove IS_CLICKED class of cells
            this.dayCells.forEach(dayCell => dayCell.classList.remove(DAY_CELL_IS_CLICKED));
        }

        cell.classList.add(DAY_CELL_IS_CLICKED);

        this._clickedCell = cell;
        this._value = cell.date.uts;
        this.selectedDateChange();
        this._keyboardPreselectedCell = null;

        this._hoverCell(cell);
    }

    /**
     * When selected date change (user click on a calendar cell)
     * Interval selection pattern with @param date which can be:
     * 1) @name startDate if drag isn't in progress
     * 2) @name endDate if drag is in progress
     * 3) @name startDate if drag is in progress 
     * 3-a) but is an earlier date than the @name startDate (before: reset selection)
     * 3-b) but is an later date than the @name endDate (after: reset selection)
     */
    public override selectedDateChange(): void {

        if (this._value == null) { return; }

        // Check if date from click is after starting edge
        const isDateUpperThanStartDate: boolean | undefined = this._selectedRangeDates.start && isAfter(this._value, this._selectedRangeDates.start);

        // Check if date from click is before ending edge
        const isDateLowerThanEndDate: boolean | undefined = this._selectedRangeDates.finish && isAfter(this._selectedRangeDates.finish, this._value);

        if (this.dragSelection.isOccurring) {

            /**
             * * On finish drag
             * If @this this._dragSelection.isOccurring is True then block the edge manipulated
             * @this _selectedRangeDates to store date as current range edge
             * @this _startCell or @this _endCell to keep cell reference and set display
             */
            this.dragSelection.isOccurring = false;

            // Update selected date value
            if (this.dragSelection.isUpdating === "startDate") {
                this._selectedRangeDates.start = this._value;
                this._startCell = this._getCellByDate(this._value);
                if (this._startCell) {
                    this._startCell.classList.toggle(DAY_CELL_START_RANGE);
                }
            } else if (this.dragSelection.isUpdating === "endDate") {
                if (this._selectedRangeDates.start && this._value < this._selectedRangeDates.start) {
                    this._selectedRangeDates.finish = beginOfNext(this._selectedRangeDates.start, "day").uts;
                    this._selectedRangeDates.start = this._value;
                    this._startCell = this._getCellByDate(this._selectedRangeDates.start);
                    this._endCell = this._getCellByDate(this._selectedRangeDates.finish);
                    if (this._startCell) {
                        this._startCell.classList.toggle(DAY_CELL_START_RANGE);
                    }
                    if (this._endCell) {
                        this._endCell.classList.toggle(DAY_CELL_END_RANGE);
                    }
                } else {
                    this._selectedRangeDates.finish = beginOfNext(this._value, "day").uts;
                    this._endCell = this._getCellByDate(this._value);
                    if (this._endCell) {
                        this._endCell.classList.toggle(DAY_CELL_END_RANGE);
                    }
                }
            }

            // Re-set initial ranges to allow user to select any valid interval we want
            this.options.setAndRefreshIfChanged({ ranges: this._ranges });
            this._dispatchOnPickerValueChange();

        } else {
            /**
             * * On starting drag
             * @member isClickedCellStartDate to check if Start Edge is manipulated
             * @member isClickedCellEndDate to check if End Edge is manipulated
             */
            const isClickedCellStartDate = this._selectedRangeDates.start && areSame(this._value, this._selectedRangeDates.start, "day");
            const isClickedCellEndDate = this._selectedRangeDates.finish && areSame(this._value, subtract(this._selectedRangeDates.finish, { day: 1 }).uts, "day");

            // User can reset end date when clicked to the endDateCell again
            if ((this.options as DateIntervalPickerOptions).clickToMoveEdges && (isClickedCellStartDate || isClickedCellEndDate)) {

                // reset concerned current cell of range
                if (isClickedCellEndDate) {
                    this.dragSelection.isUpdating = "endDate";// indicate endDate is dragged edge in @this _dragSelection.updating
                    this._endCell = null;
                } else {
                    this.dragSelection.isUpdating = "startDate";// indicate startDate is dragged edge in @this _dragSelection.updating
                    this._startCell = null;
                }

                this._clickedCell = null;
            } else {
                // if the user does not click on start edge nor end edge then reset current interval 
                if (this._selectedRangeDates.start && !isDateLowerThanEndDate ||
                    this._selectedRangeDates.finish && !isDateUpperThanStartDate
                ) {
                    this.clearSelection();
                }

                // Update selected start date value
                this._selectedRangeDates.start = this._value;
                this._startCell = this._getCellByDate(this._value);
                if (this._startCell) {
                    this._startCell.classList.toggle(DAY_CELL_START_RANGE);
                }
                this.dragSelection.isUpdating = "endDate";
            }

            const value = this._value;
            const rangeOfDate = value != null ? this.options.ranges.find(range => this._isDateInRange(value, range)) : undefined;
            if (rangeOfDate) {
                this.options.setAndRefreshIfChanged({ ranges: [rangeOfDate] });
            }

            // Change @this _dragSelection.isOccurring to return in 
            this.dragSelection.isOccurring = true;
        }

        // Always call @this _updateCellsDisplay to manage general display behavior
        this.updateCellsDisplay();

        // Dispatch date change event
        this.dateChangeEvent.emit(toDate(this._value));

    }

    /**
     * Adjust display on cell hover
     */
    protected _hoverCell(cell: HTMLDateDivElement): void {
        // If drag in progress
        if (this.dragSelection.isOccurring) {

            // Check if cell date overed is before starting edge
            const isDateLowerThanStartDate: boolean | undefined = this._selectedRangeDates.start && isAfter(this._selectedRangeDates.start, cell.date.uts);

            // Check if cell date overed is after ending edge
            const isDateUpperThanEndDate: boolean | undefined = this._selectedRangeDates.finish && isAfter(cell.date.uts, toDate(this._selectedRangeDates.finish).beginOfPrevious("day").uts);

            if (this.dragSelection.isUpdating === "startDate" && this._selectedRangeDates.finish) {

                if (isDateUpperThanEndDate) {// Date is after end date, set end from overed cell date
                    this._selectedTss = getIntersectingDays(toDate(this._selectedRangeDates.finish).beginOfPrevious("day"), cell.date, { strict: false });
                } else {// Date is before end date, set start from overed cell date
                    this._selectedTss = getIntersectingDays(cell.date, this._selectedRangeDates.finish, { strict: true });
                }

            } else if (this.dragSelection.isUpdating === "endDate" && this._selectedRangeDates.start) {

                // Date is before start date, set start from overed cell date
                if (isDateLowerThanStartDate) {
                    this._selectedTss = getIntersectingDays(cell.date, this._selectedRangeDates.start, { strict: false });
                } else {// Date is after start date, set end from overed cell date
                    this._selectedTss = getIntersectingDays(this._selectedRangeDates.start, cell.date, { strict: false });
                }
            }
        }

        // Update display
        this.updateCellsDisplay();

        // Dispatch over event
        this.dayCellOveredEvent.emit(cell.date);
    }

    /**
     * Add a click listener to a cell, used to switch between selected cells for component
     * [Override DatePicker method] - Used to add mouseenter event for cells
     * @param cell - Cell to set click listener
     */
    protected override _addCellClickListener(cell: HTMLDateDivElement): void {
        super._addCellClickListener(cell);

        cell.addEventListener("mouseenter", () => {
            this._hoverCell(cell);
        });
    }

    /**
     * Change month behavior for an element
     * [Override DatePicker method] - Used to initialize (start/end/selected)cells display after a month change event
     * @param offset - Month offset, can be a number (-1/+1) or a date from a different month than current month displayed
     * @param monthYearDiv [optional] - The div to set textContent 
     */
    protected override _changeMonthCallback(offset: number | TsDate, monthYearDiv?: HTMLElement | null): void {
        super._changeMonthCallback(offset, monthYearDiv);

        // Update start interval cell
        if (this._selectedRangeDates.start) {
            this._startCell = this._getCellByDate(this._selectedRangeDates.start);
            this._startCell?.classList.add(DAY_CELL_START_RANGE);
        }

        // Update end interval cell
        if (this._selectedRangeDates.finish) {
            this._endCell = this._getCellByDate(this._selectedRangeDates.finish, { isFinishDate: true });
            this._endCell?.classList.add(DAY_CELL_END_RANGE);
        }

        // Update selected cells
        this.updateCellsDisplay();
    }

    /**
     * Update cells display with current selectedDates 
     */
    public updateCellsDisplay(): void {
        const dayGridDiv: HTMLElement | null = this.components.dayGrid;

        // Check if grid div exist
        if (dayGridDiv == null) { return; }

        // Loop into all cells of calendar
        const cells: HTMLCollection = dayGridDiv.children;
        for (let i = 0; i < cells.length; i++) {
            const cell: HTMLDateDivElement = cells[i] as HTMLDateDivElement;

            // class tags which always needed to be removed before set on specific cells , this avoid duplicate style
            cell.classList.remove(DAY_CELL_IS_CLICKED, DAY_CELL_START_RANGE, DAY_CELL_END_RANGE);

            // Check if the cell is a cell in selection
            const isCellASelectedDate = this._selectedTss.find((ts: TsDate) => areSame(ts, cell.date, "day"));
            const isCellInRange = cell.classList.contains(DAY_CELL_SELECTABLE);

            // using startDate of @this _selectedRangeDates which contains validated interval (current or previous one) 
            if (isCellASelectedDate && isCellInRange && this._selectedRangeDates.start) {

                // Always add "selected" class if cell is inbound interval - for start & end date, class will be remove later
                if (!cell.classList.contains(OVERED_CELL_SELECTED)) {
                    cell.classList.add(OVERED_CELL_SELECTED);
                }

                // All cells of interval selection
                if (!cell.classList.contains(OVERED_CELL)) {
                    cell.classList.add(OVERED_CELL);
                }

                // First cell of interval selection
                if (this._selectedTss.length > 0 && areSame(this._selectedTss[0], cell.date, "day")) {

                    // Add "FirstCell" classes
                    cell.classList.add(OVERED_CELL_FIRST, DAY_CELL_START_RANGE);
                    cell.classList.remove(OVERED_CELL_SELECTED);

                    // If rounded option is enabled
                    if (this.options.roundedCells) {
                        cell.classList.add(OVERED_CELL_FIRST_ROUNDED);
                    }
                } else {
                    // Always remove for other cells
                    cell.classList.remove(OVERED_CELL_FIRST);
                    if (this.options.roundedCells) { cell.classList.remove(OVERED_CELL_FIRST_ROUNDED); }
                }

                // Last cell of interval selection
                // Not applying end edge style when interval contains only one day
                if (this._selectedTss.length > 1 && areSame(this._selectedTss[this._selectedTss.length - 1], cell.date, "day")) {

                    // Add "LastCell" classes
                    cell.classList.add(OVERED_CELL_LAST, DAY_CELL_END_RANGE);
                    cell.classList.remove(OVERED_CELL_SELECTED);
                    // If rounded option is enabled
                    if (this.options.roundedCells) { cell.classList.add(OVERED_CELL_LAST_ROUNDED); }
                } else {
                    cell.classList.remove(OVERED_CELL_LAST);
                    if (this.options.roundedCells) { cell.classList.remove(OVERED_CELL_LAST_ROUNDED); }
                }

                // Specific display case for one day interval (ex: user select startCell, and during drag he hover startCell again, "endCell" class round is applied)
                if (this._selectedTss.length === 1 && this.options.roundedCells) {
                    cell.classList.add(OVERED_CELL_LAST_ROUNDED);
                }

            } else {  // Reset display is cell isn't inner current selection
                this._resetCellOveredClasses(cell);
            }

        }
    }

    /** Clear the current selection, reset displays, selectedDates, start/end cells */
    public clearSelection(): void {
        this._selectedTss = [];
        this._selectedRangeDates.finish = undefined;

        // Remove css classes for all cells of calendar
        const dayGridDiv: HTMLElement | null = this.components.dayGrid;
        if (dayGridDiv) {
            const cells: HTMLCollection = dayGridDiv.children;
            for (let i = 0; i < cells.length; i++) {
                this._resetCellOveredClasses(cells[i] as HTMLDateDivElement);
            }
        }

        // Remove css classes for start/end cells
        this._startCell?.classList.remove(DAY_CELL_START_RANGE);
        this._endCell?.classList.remove(DAY_CELL_END_RANGE);

        // Reset attributes
        this._startCell = null;
        this._endCell = null;

        this.dragSelection.isOccurring = false;
        this.dragSelection.isUpdating = "startDate";
    }

    /**
     * Reset overed class display for a overed/selected cell
     */
    private _resetCellOveredClasses(cell: HTMLDateDivElement): void {
        if (cell.classList.contains(OVERED_CELL)) { cell.classList.remove(OVERED_CELL); }
        if (cell.classList.contains(OVERED_CELL_FIRST)) { cell.classList.remove(OVERED_CELL_FIRST); }
        if (cell.classList.contains(OVERED_CELL_LAST)) { cell.classList.remove(OVERED_CELL_LAST); }
        if (cell.classList.contains(OVERED_CELL_SELECTED)) { cell.classList.remove(OVERED_CELL_SELECTED); }
    }

}